import { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import NavRow1 from '@/components/nav/NavRow1'
import MobileBottomNav from '@/components/MobileBottomNav'
import { CrownProvider } from '@/lib/crownContext'
import type { User, Market, EventSummary, LeaderboardList, CasinoList, UserListItem } from '@/lib/api'
import {
  getMe,
  getMarkets,
  getEvents,
  getCategories,
  getLeaderboardList,
  getCasinoList,
  getUserList,
} from '@/lib/api'
import { apiUrl, applyRuntime } from './runtime'

interface NavData {
  user: User | null
  markets: Market[]
  events: EventSummary[]
  categories: string[]
  leaderboardList: LeaderboardList
  casinoList: CasinoList
  users: UserListItem[]
}

function NavWidget({ showSidebarTrigger = false }: { showSidebarTrigger?: boolean }) {
  const [data, setData] = useState<NavData | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      getMe(),
      getMarkets(),
      getEvents(),
      getCategories(),
      getLeaderboardList(),
      getCasinoList(),
      getUserList(),
    ]).then(([user, markets, events, categories, leaderboardList, casinoList, users]) => {
      if (cancelled) return
      setData({
        user: user as User | null,
        markets: markets as Market[],
        events: events as EventSummary[],
        categories,
        leaderboardList: leaderboardList as LeaderboardList,
        casinoList: casinoList as CasinoList,
        users: users as UserListItem[],
      })
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Live cheddar balance: the casino dispatches `weeabets:wallet-refresh` on the window after
  // every game settles (bridged from a Phoenix push_event). Re-fetch just the user so the
  // balance in Row 1 updates as results resolve, without a full page reload.
  useEffect(() => {
    let cancelled = false
    async function refreshUser() {
      const user = (await getMe()) as User | null
      if (!cancelled) setData(prev => (prev ? { ...prev, user } : prev))
    }
    window.addEventListener('weeabets:wallet-refresh', refreshUser)
    return () => {
      cancelled = true
      window.removeEventListener('weeabets:wallet-refresh', refreshUser)
    }
  }, [])

  const loginUrl = apiUrl('/api/auth/discord')

  // No wrapping <header> here: the casino host wraps this mount point in a sticky, border-less,
  // max-width container (its root layout) and renders its own Row 2 (game_nav, which carries the
  // bar's bottom border) directly below — so the two rows read as one unified bar with no divider
  // between them, matching core's Navbar (epic #33).
  return data ? (
    // CrownProvider (widget shim → core's public crown API) makes the holder's logo gold.
    <CrownProvider>
      <NavRow1
        user={data.user}
        markets={data.markets}
        events={data.events}
        categories={data.categories}
        leaderboardList={data.leaderboardList}
        casinoList={data.casinoList}
        users={data.users}
        loginUrl={loginUrl}
        showSidebarTrigger={showSidebarTrigger}
      />
    </CrownProvider>
  ) : (
    // Skeleton matching Row 1's height so the casino content doesn't jump on hydrate.
    <div className="flex items-center gap-2 py-3" aria-hidden="true">
      <span className="text-xl font-bold tracking-tight fancy-gradient-text">Weeabets</span>
      <div className="ml-auto h-9 w-24 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse" />
    </div>
  )
}

function mount() {
  const el = document.getElementById('weeabets-nav')
  if (el) {
    applyRuntime(el)

    // The casino opts in (data-sidebar="true") to show the mobile sidebar trigger, which opens
    // the separately-mounted sidebar widget via a window event. Defaults off (core's own nav and
    // any existing embed are unaffected).
    const showSidebarTrigger = el.dataset.sidebar === 'true'

    createRoot(el).render(<NavWidget showSidebarTrigger={showSidebarTrigger} />)
  }

  // The mobile bottom nav is a separate body-level mount (it's `fixed`, not part of Row 1's
  // flow) so the casino can opt in independently of the header. It needs no data of its own —
  // just the shared runtime config for its Link hrefs to resolve against core's origin.
  const bottomNavEl = document.getElementById('weeabets-bottom-nav')
  if (bottomNavEl) {
    applyRuntime(bottomNavEl)
    createRoot(bottomNavEl).render(<MobileBottomNav />)
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount)
  } else {
    mount()
  }
}
